import { useQuery } from '@tanstack/react-query';
import cx from 'classnames';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { ReactNode, useMemo } from 'react';

import useLinkProps from '../../../hooks/use-link-props';
import getVehicle from '../../../utils/react-query/query-options/get-vehicle';
import SearchBadges from '../../modules/search-badges';
import getCategoryBySlug from '../vehicle/utils/get-category-by-slug';
import TextLink from '../../ui/links/text';
import FeaturedLink from '../../ui/links/featured';

const ChoiceModal = ({
  groups,
  extraContent,
  zeroResults = false,
  zeroResultsContent,
  products,
  searchChoice = true,
}: {
  groups: TCategories;
  products: any;
  extraContent: ReactNode;
  zeroResultsContent: ReactNode;
  zeroResults?: boolean;
  searchChoice?: boolean;
}) => {
  const { locale = 'en', push, query } = useRouter();
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const { category, subCategory, vehicle } = query;

  const categoryName = useMemo(() => {
    if (!category || !subCategory) {
      return null;
    }
    return getCategoryBySlug(
      {
        category: category as string,
        subCategory: subCategory as string,
      },
      groups
    );
  }, [category, subCategory, groups]);

  const { data: vehicleInfo } = useQuery(...getVehicle({ locale, vehicle }), {
    enabled: !!vehicle,
    keepPreviousData: true,
  });

  const { reference, askSearchChoice, page, ...queryToKeep } = query;

  const pointsToSearch =
    (!!categoryName && !!vehicle && !!vehicleInfo) ||
    (query.vehicle && query.category && query.reference);

  return (
    <div
      className={cx('max-lg:text-left', {
        'max-lg:pb-4': zeroResultsContent,
        'max-lg:px-4 max-lg:pt-4':
          zeroResultsContent ||
          (!zeroResults && searchChoice && (pointsToSearch || extraContent)) ||
          (pointsToSearch && searchChoice) ||
          extraContent,
      })}
    >
      {zeroResultsContent}
      {!zeroResults && searchChoice && (pointsToSearch || extraContent) && (
        <>
          <h3 className="font-extrabold text-[32px] mb-2">
            <Trans
              i18nKey="products:searchChoice.title"
              components={{
                b: <span className="font-bold" />,
              }}
            />
          </h3>
        </>
      )}

      <div>
        {pointsToSearch && searchChoice && (
          <>
            {reference && (vehicle || categoryName) && (
              <span>{t('products:searchChoice.moreResults')}</span>
            )}

            <ul className="mb-4 pl-4 border-l-bparts-100 border-l-4">
              <li className="mb-2 max-lg:text-left">
                {!!categoryName && !!vehicle && !!vehicleInfo && (
                  <div className="mt-4">
                    <FeaturedLink
                      as="button"
                      type="button"
                      onClick={() => {
                        const {
                          reference,
                          askSearchChoice,
                          page,
                          ...queryToKeep
                        } = query;
                        const link = getLink('search', {
                          ...queryToKeep,
                          //searchChoice: ['vehicle', 'subCategory', 'reference'],
                        });

                        push(link.href, link.as, {
                          shallow: true,
                          scroll: true,
                        });
                      }}
                      className="max-lg:text-left"
                    >
                      {t('products:searchChoice.part-vehicle', {
                        part: categoryName.subCategory,
                        vehicle: `${vehicleInfo.info.brand.name || ''} ${vehicleInfo.info?.model?.name || ''
                          } ${vehicleInfo.info?.version
                            ? vehicleInfo.info.version.name
                            : ''
                          }`,
                      })}
                    </FeaturedLink>
                  </div>
                )}
              </li>
              <li className="mb-2 max-lg:text-left">
                {query.vehicle && query.category && query.reference && (
                  // vehicle + reference
                  <FeaturedLink
                    as="button"
                    className="max-lg:text-left"
                    type="button"
                    onClick={() => {
                      const {
                        category,
                        subCategory,
                        askSearchChoice,
                        page,
                        ...queryToKeep
                      } = query;
                      const link = getLink('search', {
                        ...queryToKeep,
                        //searchChoice: ['reference'],
                      });

                      push(link.href, link.as, { shallow: true, scroll: true });
                    }}
                  >
                    {!!vehicleInfo &&
                      t('products:searchChoice.ref-vehicle', {
                        ref: query.reference,
                        vehicle: `${vehicleInfo.info.brand.name} ${vehicleInfo.info?.model?.name || ''
                          } ${vehicleInfo.info?.version
                            ? vehicleInfo.info.version.name
                            : ''
                          }`,
                      })}
                  </FeaturedLink>
                )}
              </li>
              <li className="mb-2 max-lg:text-left">
                {query.vehicle && query.category && query.reference && (
                  // part + reference
                  <FeaturedLink
                    as="button"
                    type="button"
                    onClick={() => {
                      const { vehicle, askSearchChoice, page, ...queryToKeep } =
                        query;
                      const link = getLink('search', {
                        ...queryToKeep,
                        //searchChoice: ['reference'],
                      });

                      push(link.href, link.as, { shallow: true, scroll: true });
                    }}
                    className="max-lg:text-left"
                  >
                    {t('products:searchChoice.ref-part', {
                      ref: query.reference,
                      part: categoryName.subCategory,
                    })}
                  </FeaturedLink>
                )}
              </li>
            </ul>
          </>
        )}
      </div>

      {extraContent && <div className="">{extraContent}</div>}
    </div>
  );
};

export default ChoiceModal;
